import { Component, inject, OnInit, signal } from '@angular/core';
import { catchError, of, switchMap } from 'rxjs';
import { TenantsService } from './core/services/tenants.service';
import { MainLayoutComponent } from './core/components/layouts/main-layout/main-layout.component';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SpinnerComponent } from '@ui';
import { NgClass } from '@angular/common';
import { CONFIG_SERVICE } from '@auth';
import { PermissionsService } from './core/services/permissions.service';
import { environment } from '../environments/environment';
import { SpinnerController } from './core/services/spinner.controller';

@Component({
  selector: 'app-root',
  imports: [MainLayoutComponent, RouterOutlet, SpinnerComponent, NgClass],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [],
})
export class AppComponent implements OnInit {
  private tenantsService = inject(TenantsService);
  private permissionsService = inject(PermissionsService);

  isSpinning = inject(SpinnerController).isSpinning;

  configService = inject(CONFIG_SERVICE, { optional: true });
  showMenu = signal<boolean>(true);
  public favIcon: any = document.querySelector('#appIcon');

  constructor(private router: Router) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentRoute = this.router.routerState.root.firstChild;
        const showMenu = currentRoute?.snapshot.data['showMenu'] ?? true;
        this.showMenu.set(showMenu);
      }
    });
  }

  ngOnInit() {
    this.favIcon.href = environment.imagebaseUrl + 'icon.ico';
    this.tenantsService
      .getUserTenants()
      .pipe(
        catchError(() => {
          this.tenantsService.clearSelectedTenants();
          return of([]);
        }),
      )
      .subscribe();
    // If the configService is available, obtain the authentication token
    if (this.configService) {
      this.configService
        .getAuthAccessToken()
        .pipe(
          switchMap((token: string | null) => {
            // If a token is present, decode and extract permissions
            if (token) {
              const user = this.decodeJwtPayload(token);
              // Extract permissions from the user object, defaulting to an empty array if not present
              let permissions = user?.permissions || [];
              // If permissions is a string, convert it to an array
              if (typeof permissions === 'string') {
                permissions = [permissions];
              }
              // Store the permissions array and notify via PermissionsService
              sessionStorage.setItem(
                'permissions',
                JSON.stringify(permissions),
              );
              this.permissionsService.setPermissions(permissions);
              localStorage.setItem('centerPeriod', 'last24h');
              return of(token);
            }
            // If no token, return null
            return of(null);
          }),
        )
        .subscribe();
    }
  }

  decodeJwtPayload(token: string): any {
    if (!token) return null;
    // JWT tokens have three parts separated by dots: header.payload.signature
    // The payload (token.split('.')[1]) contains the user data in base64
    const payload = token.split('.')[1];
    // Base64url to base64
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Padding
    while (base64.length % 4) {
      base64 += '=';
    }
    try {
      return JSON.parse(decodeURIComponent(escape(window.atob(base64))));
    } catch (e) {
      console.error('Invalid JWT payload', e);
      return null;
    }
  }
}
